/**
 * Product Gallery Accessibility
 *
 * Main improvements:
 * - allows the main product image to open the lightbox with Enter or Space;
 * - removes hidden/off-screen main gallery slides from keyboard navigation;
 * - keeps only the currently active slide available to assistive technology.
 */

(function () {
  let galleryUpdateScheduled = false;

  /**
   * Opens the product image lightbox when the focused main image
   * is activated with Enter or Space.
   */
  function initGalleryLightboxKeyboardSupport() {
    document.addEventListener("keydown", function (event) {
      const mainMedia = event.target.closest(
        ".as-prod__main-slide .m-product-media",
      );

      if (!mainMedia) return;
      if (mainMedia.closest(".as-prod__mini-slide")) return;
      if (event.key !== "Enter" && event.key !== " ") return;

      const slide = mainMedia.closest(".as-prod__main-slide");
      const gallery = mainMedia.closest("media-gallery");

      if (!slide || !gallery || !gallery.lightbox) return;

      const index = Number(slide.dataset.index);

      if (Number.isNaN(index)) return;

      event.preventDefault();
      event.stopPropagation();

      gallery.lightbox.loadAndOpen(index);
    });
  }

  /**
   * Adds inert and aria-hidden to all inactive main gallery slides.
   * This prevents Tab from moving through hidden product images
   * and any interactive controls inside those images.
   */
  function syncMainGallerySlides() {
    document
      .querySelectorAll(".as-prod__media-slider")
      .forEach(function (slider) {
        const slides = slider.querySelectorAll(".as-prod__main-slide");

        slides.forEach(function (slide) {
          const isActive =
            slide.classList.contains("swiper-slide-active") &&
            !slide.hidden &&
            window.getComputedStyle(slide).display !== "none";

          if (isActive) {
            slide.removeAttribute("inert");
            slide.removeAttribute("aria-hidden");
          } else {
            slide.setAttribute("inert", "");
            slide.setAttribute("aria-hidden", "true");
          }
        });
      });
  }

  function scheduleGallerySync() {
    if (galleryUpdateScheduled) return;

    galleryUpdateScheduled = true;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        galleryUpdateScheduled = false;
        syncMainGallerySlides();
      });
    });
  }

  function initHiddenGallerySlidesAccessibility() {
    scheduleGallerySync();

    /*
     * Swiper changes the active image by updating slide classes.
     */
    const galleryObserver = new MutationObserver(function (mutations) {
      const shouldSync = mutations.some(function (mutation) {
        const target = mutation.target;

        return (
          target instanceof Element && target.closest(".as-prod__media-slider")
        );
      });

      if (shouldSync) {
        scheduleGallerySync();
      }
    });

    galleryObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden"],
    });

    /*
     * Variant changes can replace or filter gallery images.
     */
    document.addEventListener("change", function (event) {
      if (!event.target.closest("variant-picker")) return;

      scheduleGallerySync();
      window.setTimeout(scheduleGallerySync, 150);
      window.setTimeout(scheduleGallerySync, 400);
    });

    /*
     * Previous, Next and thumbnail controls change the active image.
     */
    document.addEventListener("click", function (event) {
      if (event.target.closest(".as-prod__slider-nav, .as-prod__mini-slide")) {
        scheduleGallerySync();
        window.setTimeout(scheduleGallerySync, 100);
      }
    });
  }

  function initGalleryAccessibility() {
    initGalleryLightboxKeyboardSupport();
    initHiddenGallerySlidesAccessibility();
  }

  window.ASA11y.onReady(initGalleryAccessibility);
})();
